import React, { useRef } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";

import { TypeEnum } from "@/src/api/ProjectAPI.types";

import OffersForm from "./OffersForm";
import OfferAPI from "../../api/OfferAPI";
import "../../css/admin/offers.scss";
import { eduOfferPlanToApi } from "../../utils/eduOfferPlan";
import history from "../common/navigation/AppHistory";
import { PROJECTS_ROUTE } from "../routes/routes";
import Spinner from "../utils/Spinner";

import type { JSX } from "react";

import type { UpdatedOffer } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";

export type OfferPageAction = "edit" | "new";

function OffersEdit(): JSX.Element {
  const { t } = useTranslation(["editor-offers"]);
  const languages = useSelector((state: AppState) => state.languages);
  const currentLanguageUuid = languages?.current?.uuid;
  const isEduProject = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.type === TypeEnum.Edu,
  );

  const {
    projectSlug,
    action,
    id: offerId,
  } = useParams<{ projectSlug: string; action: OfferPageAction; id: string }>();

  const queryClient = useQueryClient();

  const showSuccessMessage = (message: string): void => {
    toast.success(message);
  };

  const showErrorMessage = (message: string): void => {
    toast.error(message);
  };

  const getAdvertisingListQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getAdvertisingList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const getOfferQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOffer,
    params: [
      {
        offerId,
        projectSlug,
      },
    ],
  });

  const getOffersQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOffers,
    params: [{ projectSlug }],
  });

  const { data: offerData, isLoading: getOfferIsLoading } = useQuery({
    ...getOfferQuery,
    onError: () => {
      showErrorMessage(t("editor-notif:fetchOfferError"));
      history.push(`${PROJECTS_ROUTE}/${projectSlug}/offers`);
    },
    enabled: !!offerId && action !== "new",
  });

  const updateOfferMutation = useMutation({
    mutationFn: async (offer: UpdatedOffer) =>
      OfferAPI.updateOffer({ offer, projectSlug }),
    onSuccess: () => {
      showSuccessMessage(t("editor-notif:saveSuccess"));
      queryClient.invalidateQueries({
        queryKey: getOfferQuery.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getOffersQuery.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getAdvertisingListQuery.queryKey,
      });
    },
    onError: () => showErrorMessage(t("editor-notif:saveError")),
  });

  // Must not fail the offer creation, whose onError redirects to the list.
  const createEduOfferPlan = async (offerUuid: string): Promise<void> => {
    try {
      await OfferAPI.createOfferPlan({
        projectSlug,
        plan: eduOfferPlanToApi(offerUuid),
      });
    } catch {
      showErrorMessage(t("editor-notif:createOfferPlanError"));
    }
  };

  const createOfferMutation = useMutation({
    mutationFn: async () => {
      const offer = await OfferAPI.createOffer({
        t,
        languageUUID: currentLanguageUuid,
        projectSlug,
      });

      if (isEduProject) await createEduOfferPlan(offer.uuid);

      return offer;
    },
    onSuccess: (data) => {
      showSuccessMessage(t("editor-notif:createOfferSuccess"));
      queryClient.invalidateQueries(getOffersQuery.queryKey);
      history.replace(
        `${PROJECTS_ROUTE}/${projectSlug}/offers/edit/${data.uuid}`,
      );
    },
    onError: () => {
      showErrorMessage(t("editor-notif:createOfferError"));
      history.replace(`${PROJECTS_ROUTE}/${projectSlug}/offers`);
    },
  });

  const hasCreatedRef = useRef(false);

  useMount(() => {
    if (action === "new" && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      createOfferMutation.mutate();
    }
  });

  const itemIsValide = (item: UpdatedOffer): string[] => {
    const errorMessages: string[] = [];

    item.translations.forEach((it) => {
      if (!it.name || it.name.length <= 0) {
        errorMessages.push(t("editor-errors:noName").toLowerCase());
      }
      if (!it.description || it.description.length <= 0) {
        errorMessages.push(t("editor-errors:noDescription").toLowerCase());
      }
    });

    item.offer_plans.forEach((offerPlan) => {
      if (
        (offerPlan.platform === "apple" || offerPlan.platform === "google") &&
        !offerPlan.platform_product_id
      ) {
        errorMessages.push(t("editor-errors:noSku").toLowerCase());
      }
    });

    const noProviderPlan = item.offer_plans.some((offerPlan) => {
      const isPaid = offerPlan.type !== "free";
      return (
        isPaid &&
        offerPlan.platform === "okast" &&
        offerPlan.provider_plans?.length === 0
      );
    });

    if (noProviderPlan) {
      errorMessages.push(t("editor-errors:noProviderPlan").toLowerCase());
    }

    return errorMessages;
  };

  const handleOfferFormSubmit = async (
    offer: UpdatedOffer,
    disableValidation: boolean,
  ): Promise<void> => {
    const errorMessages = disableValidation ? [] : itemIsValide(offer);

    if (errorMessages.length === 0) {
      await updateOfferMutation.mutateAsync(offer);
    } else {
      const errorDetails = errorMessages.join(", ");
      showErrorMessage(
        `${t("editor-notif:saveError")}${
          errorDetails ? ` : ${errorDetails}` : ""
        }`,
      );
    }
  };

  return (
    <div className="editor-offer-edit-container">
      {getOfferIsLoading || !offerData ? (
        <div className="admin-user-form_spinner-container">
          <Spinner />
        </div>
      ) : (
        <OffersForm
          action={action || "edit"}
          item={offerData}
          onSubmit={handleOfferFormSubmit}
        />
      )}
    </div>
  );
}

export default OffersEdit;
